import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiObject from 'api/apiObjects';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useObjectCard } from '../context/ObjectCardProvider';
import { addProposalExtraAsOtherExpend } from '../Proposal2Warehouse/model/addProposalExtraAsOtherExpend';
import type { AddProductsSourceKey } from '../Tabs/ProductsTableBody/model/useAddProductsTransfer';
import type { ProposalShortageItem } from './model/types';
import SolarIcon from 'components/icons/SolarIcon';

const SOURCE_META: Record<
    AddProductsSourceKey,
    { titleKey: string; hintKey: string; icon: string }
> = {
    reserve: {
        titleKey: 'addProducts.fromReserve',
        hintKey: 'addProducts.fromReserveHint',
        icon: 'bi-bookmark-star',
    },
    team: {
        titleKey: 'addProducts.fromTeam',
        hintKey: 'addProducts.fromTeamHint',
        icon: 'bi-people',
    },
    main: {
        titleKey: 'addProducts.fromMain',
        hintKey: 'addProducts.fromMainHint',
        icon: 'bi-building',
    },
};

interface ProductSourceOption {
    key: AddProductsSourceKey;
}

interface ShortageProductAddProps {
    productId: number;
    sources: ProductSourceOption[];
    teamName: string;
    canAdd: boolean;
    openTransfer: (key: AddProductsSourceKey, productId?: number) => void;
}

export const ShortageProductAdd = ({
    productId,
    sources,
    teamName,
    canAdd,
    openTransfer,
}: ShortageProductAddProps) => {
    const { t } = useTranslation(['Objects']);
    const { isEditLocked } = useObjectCard();

    if (isEditLocked || !canAdd || !(productId > 0)) return null;

    const hintFor = (key: AddProductsSourceKey) => {
        if (key === 'team') {
            const team = teamName
                ? t('addProducts.fromTeamName', { name: teamName })
                : '';
            return t('addProducts.fromTeamHint', { team });
        }
        return t(SOURCE_META[key].hintKey);
    };

    const addLabel = t('proposalShortage.btnAdd');

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu text-start"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {sources.map((source) => {
                const meta = SOURCE_META[source.key];
                return (
                    <Dropdown.Item
                        key={source.key}
                        as="button"
                        onClick={(e) => {
                            openTransfer(source.key, productId);
                            e.currentTarget.blur();
                        }}
                    >
                        <div className="d-flex align-items-start w-100 text-start">
                            <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                                <SolarIcon name={meta.icon} />
                            </span>
                            <div className="flex-grow-1 min-w-0">
                                <div className="fw-medium">{t(meta.titleKey)}</div>
                                <small className="text-muted warehouse-team-menu-hint">
                                    {hintFor(source.key)}
                                </small>
                            </div>
                        </div>
                    </Dropdown.Item>
                );
            })}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                size="sm"
                variant="outline-primary"
                className="proposal-shortage__add-btn p-0"
                title={addLabel}
                aria-label={addLabel}
            >
                <SolarIcon name="plus-lg" aria-hidden="true" />
            </Dropdown.Toggle>
            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

interface ShortageServiceAddProps {
    item: ProposalShortageItem;
    proposalName: string;
}

export const ShortageServiceAdd = ({ item, proposalName }: ShortageServiceAddProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const { objectId, vObject, fetchObject, isEditLocked } = useObjectCard();
    const params = useSettingsParams();
    const [adding, setAdding] = useState(false);

    if (isEditLocked) return null;

    const handleAddService = async () => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId || adding) return;

        const count = Number(item.deficit || 0);
        if (!(count > 0)) {
            showToast(t('messErrorExpandCount'), 'danger');
            return;
        }

        const rate = Number(localSettings?.RATE?.current || 1) || 1;
        const price = Number(item.price || 0);
        const priceSale = Number(item.priceSale || 0) || price;
        const priceRate =
            item.priceRate != null ? Number(item.priceRate) : price * rate;
        const priceSaleRate =
            item.priceSaleRate != null ? Number(item.priceSaleRate) : priceSale * rate;

        setAdding(true);
        const ok = item.asOtherExpense && params.includeOtherInObjectCost
            ? await addProposalExtraAsOtherExpend({
                  objectId: targetObjectId,
                  itemName: item.name,
                  itemCount: count,
                  price,
                  priceRate,
                  priceSale,
                  priceSaleRate,
              })
            : await apiObject.addExpendTeam({
                  teamId: Number(vObject?.object?.teamId || 0),
                  objectId: targetObjectId,
                  serviceId: Number(item.serviceId || 0),
                  name: item.name,
                  count,
                  price,
                  priceRate,
                  priceSale,
                  priceSaleRate,
                  description: proposalName || '',
              });
        if (ok) {
            showToast(t('messExpandAdd'), 'success');
            if (fetchObject) await fetchObject();
        } else {
            showToast(t('common:messError'), 'danger');
        }
        setAdding(false);
    };

    const addLabel = t('proposalShortage.btnAdd');

    return (
        <Button
            size="sm"
            variant="outline-primary"
            className="proposal-shortage__add-btn p-0"
            disabled={adding}
            title={addLabel}
            aria-label={addLabel}
            onClick={() => void handleAddService()}
        >
            <SolarIcon name="plus-lg" aria-hidden="true" />
        </Button>
    );
};
